"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { closeModal } from "@/src/store/slices/allModalSlice";
import type { CustomModalSize, HeroUIModalSize } from "@/src/store/slices/allModalSlice";
import { RootState } from "@/src/store/store";
import { Button, Modal, useOverlayState } from "@heroui/react";
import { useDispatch, useSelector } from "react-redux";
import ProductQuickViewModal from "./ProductQuickViewModal";
import LoginSignupIndex from "./auth/LoginSignupIndex";
import RemoveCartItemModal from "./RemoveCartItemModal";
import DeleteAddressModal from "./DeleteAddressModal";
import DeleteAccountModal from "./DeleteAccountModal";
import LogoutConfirmModal from "./LogoutConfirmModal";
import CancelOrderModal from "./CancelOrderModal";

const HEROUI_SIZES = new Set<string>(["xs", "sm", "md", "lg", "full", "cover"]);

const CUSTOM_SIZE_CLASSES: Record<CustomModalSize, string> = {
    "xl": "max-w-xl",
    "2xl": "max-w-2xl",
    "3xl": "max-w-3xl",
    "4xl": "max-w-4xl",
    "5xl": "max-w-5xl",
};

export function ModalComponent() {
    const dispatch = useDispatch();
    const pathname = usePathname();
    const prevPathname = useRef(pathname);
    const { isOpen, componentName, modalSize, modalPadding, data, hideCloseButton, scrollable } = useSelector(
        (state: RootState) => state.allCommonModal
    );

    useEffect(() => {
        if (prevPathname.current !== pathname && isOpen) {
            dispatch(closeModal());
        }
        prevPathname.current = pathname;
    }, [pathname, isOpen, dispatch]);

    const overlayState = useOverlayState({
        isOpen,
        onOpenChange: (open) => {
            if (!open) dispatch(closeModal());
        },
    });

    const isHeroUISize = modalSize && HEROUI_SIZES.has(modalSize);
    const containerSize = isHeroUISize ? (modalSize as HeroUIModalSize) : undefined;
    const dialogClassName = !isHeroUISize && modalSize
        ? CUSTOM_SIZE_CLASSES[modalSize as CustomModalSize]
        : undefined;

    const renderComponent = () => {
        switch (componentName) {
            case "ProductQuickViewModal":
                return <ProductQuickViewModal />;
            case "LoginSignupIndex":
                return <LoginSignupIndex />;
            case "RemoveCartItemModal":
                return <RemoveCartItemModal />;
            case "DeleteAddressModal":
                return <DeleteAddressModal />;
            case "DeleteAccountModal":
                return <DeleteAccountModal />;
            case "LogoutConfirmModal":
                return <LogoutConfirmModal />;
            case "CancelOrderModal":
                return <CancelOrderModal />;
            default:
                return null;
        }
    };

    return (
        <>
            <Modal state={overlayState}>
                {/* Satisfies PressResponder from DialogTrigger in controlled (trigger-less) mode */}
                <Button aria-hidden="true" isDisabled className="sr-only absolute" />
                <Modal.Backdrop isDismissable={false}>
                    <Modal.Container size={containerSize} placement="center">
                        <Modal.Dialog className={`${dialogClassName} ${modalPadding}`}>
                            {!hideCloseButton && <Modal.CloseTrigger className="z-10" />}
                            <Modal.Body className={scrollable === false ? "overflow-hidden" : "overflow-y-auto"}>
                                {renderComponent()}
                            </Modal.Body>
                        </Modal.Dialog>
                    </Modal.Container>
                </Modal.Backdrop>
            </Modal>
        </>
    );
}
